import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Star, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import type { CareTeamMemberDetailViewModel } from '@/models/patient';

import { NameOrSkeleton } from './NameOrSkeleton';
import type { ResolvedMemberName } from './utils';

interface MemberListProps {
  readonly members: readonly CareTeamMemberDetailViewModel[];
  readonly resolveName: (m: CareTeamMemberDetailViewModel) => ResolvedMemberName;
  readonly onPromote: (userId: string) => void;
  readonly onRemove: (memberId: string) => void;
  readonly onRoleChange: (memberId: string, role: string) => void;
  readonly busy: boolean;
}

/** Supporting care-team members — the primary lives in its own slot above. */
export function MemberList({
  members,
  resolveName,
  onPromote,
  onRemove,
  onRoleChange,
  busy,
}: MemberListProps): ReactElement {
  const { t } = useTranslation();
  if (members.length === 0) {
    return (
      <p className="text-[12px] italic text-muted-foreground">
        {t('patient.careTeam.teamEmpty')}
      </p>
    );
  }
  return (
    <ul className="space-y-1.5">
      {members.map((m) => (
        <li
          key={m.id}
          className="flex items-center gap-2 rounded-md border border-border bg-card px-2.5 py-1.5"
        >
          <span className="text-[13px] text-foreground truncate min-w-0 flex-1">
            <NameOrSkeleton resolved={resolveName(m)} />
          </span>
          <Input
            type="text"
            defaultValue={m.role}
            placeholder={t('patient.careTeam.rolePlaceholder')}
            className="h-7 w-32 text-xs"
            onBlur={(event) => {
              const next = event.target.value.trim();
              if (next !== m.role) onRoleChange(m.id, next);
            }}
          />
          {!m.isPrimary && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                onPromote(m.userId);
              }}
              disabled={busy}
              aria-label={t('patient.careTeam.promoteAriaLabel')}
              className="h-7 w-7 p-0"
            >
              <Star className="h-3.5 w-3.5" aria-hidden="true" />
            </Button>
          )}
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => {
              onRemove(m.id);
            }}
            disabled={busy}
            aria-label={t('patient.careTeam.removeAriaLabel')}
            className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
          >
            <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
          </Button>
        </li>
      ))}
    </ul>
  );
}
